/* Advertorial landing page behavior: countdown, inline videos, sticky call to action. */
(() => {
  const pad = (n) => String(n).padStart(2, '0');

  class AdvCountdown extends HTMLElement {
    connectedCallback() {
      this.digits = {
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      this.timer = this.querySelector('.adv-cd__timer');
      const duration = Number(this.dataset.duration) || 0;
      if (this.dataset.mode === 'date' && Date.parse(this.dataset.end)) {
        this.end = Date.parse(this.dataset.end);
      } else {
        // Evergreen: counts down from page load, so every visit starts fresh.
        this.end = performance.timeOrigin + duration * 1000;
      }
      this.duration = duration;
      this.tick();
      this.interval = setInterval(() => this.tick(), 250);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    tick() {
      let remaining = Math.floor((this.end - Date.now()) / 1000);
      if (remaining <= 0) {
        const onEnd = this.dataset.onEnd;
        if (onEnd === 'restart' && this.duration > 0) {
          this.end = Date.now() + this.duration * 1000;
          remaining = this.duration;
        } else {
          remaining = 0;
          if (onEnd === 'hide' && this.timer) this.timer.hidden = true;
        }
      }
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      this.set(this.digits.h, h > 99 ? String(h) : pad(h));
      this.set(this.digits.m, pad(m));
      this.set(this.digits.s, pad(s));
    }

    set(el, value) {
      if (el && el.textContent !== value) el.textContent = value;
    }
  }

  class AdvVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.adv-vid__btn');
      if (this.button) {
        this.onToggle = (event) => {
          if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.toggle();
        };
        this.button.addEventListener('click', this.onToggle);
        this.button.addEventListener('keydown', this.onToggle);
      }
      if (!this.video) return;
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.load();
        }
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button && this.onToggle) {
        this.button.removeEventListener('click', this.onToggle);
        this.button.removeEventListener('keydown', this.onToggle);
      }
    }

    load() {
      if (this.loaded || !this.video) return;
      this.loaded = true;
      this.observer.disconnect();
      for (const source of this.video.querySelectorAll('source[data-src]')) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      }
      this.video.load();
      if (!this.classList.contains('is-paused')) this.play();
    }

    play() {
      const attempt = this.video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }

    toggle() {
      const paused = !this.classList.contains('is-paused');
      this.classList.toggle('is-paused', paused);
      if (this.button) this.button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      if (!this.video) return;
      if (paused) this.video.pause();
      else {
        this.load();
        this.play();
      }
    }
  }

  // Fixed to the bottom of the screen until its place at the end of the
  // section scrolls into view, where it docks (same as position: sticky).
  class AdvSticky extends HTMLElement {
    connectedCallback() {
      this.ghost = this.parentElement && this.parentElement.querySelector('.adv-sticky--ghost');
      if (!this.ghost) return;
      this.update = this.update.bind(this);
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule);
      // Page height can change without scrolling (fonts, images loading).
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(document.body);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    update() {
      const docked = this.ghost.getBoundingClientRect().bottom <= window.innerHeight + 0.5;
      this.classList.toggle('is-docked', docked);
    }
  }

  if (!customElements.get('adv-countdown')) customElements.define('adv-countdown', AdvCountdown);
  if (!customElements.get('adv-video')) customElements.define('adv-video', AdvVideo);
  if (!customElements.get('adv-sticky')) customElements.define('adv-sticky', AdvSticky);
})();
